import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  TitleRow,
  ContainerRow,
  ControlsDiV
} from '../styledComponents';
import CustomTypography from '../../CustomComponents/CustomTypography';
import { useSelector } from 'react-redux';
import PageTitle from '../../../Utils/PageTitle';
import { SearchOutlined, DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import { billiersJobList, assignJobtobiller, releaseJobFromBiller } from '../../../Apis/Biller';
import { notification, DatePicker } from 'antd';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import BillerTanStackTable from './BillerTanStackTable';
import BillerColumnSettingsModal, { BILLER_DEFAULT_COLUMNS } from './BillerColumnSettingsModal';
import { getColumnPreferences, saveColumnPreferences } from '../../../Apis/Clinic';
import {
  SearchJobListStyle,
  SettingsButton,
  StyledJobListTableContainer
} from '../JobList/styledComponents';
import { StyledListTab as StyledListTabBase } from '../Billers/styledComponents';
import { StyledJobListTabPane } from '../JobList/styledComponents.js';
import TanStackUserTable from '../../CustomComponents/TanStackUserTable';
import { createGroupQueueColumns } from './groupQueueColumnConfig';
import styled from 'styled-components';

const StyledListTab = styled(StyledListTabBase)`
  &.ant-tabs {
    overflow: visible;
  }
  & .ant-tabs-content-holder {
    overflow: visible;
  }
`;

const BILLER_PAGE_NAME = 'BILLER_JOB_LIST';

const Biller = () => {
  const [theme] = useSelector(state => [state.themeReducer]);
  const [searchVal, setSearchVal] = useState('');
  const [activeTab, setActiveTab] = useState('myJobs');
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [billers, setBillers] = useState([]);
  const [sessionId, setSessionId] = useState();
  const [exportFunction, setExportFunction] = useState(null);
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({ ...BILLER_DEFAULT_COLUMNS });
  const [dateRange, setDateRange] = useState([null, null]);
  const [pickerDisplayValue, setPickerDisplayValue] = useState([null, null]);
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);
  const [groupQueueSearchVal, setGroupQueueSearchVal] = useState('');
  const user = useSelector(state => state.userReducer);

  useEffect(() => {
    if (user) {
      setSessionId(user.sessionId);
    }
  }, [user]);

  useEffect(() => {
    fetchColumnPreferences();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const groupQueueJobs = useMemo(
    () => billers.filter(j => j.assignedBiller?.billerGroups?.length > 0),
    [billers]
  );

  const handleClaim = jobId => {
    assignJobtobiller(jobId)
      .then(response => {
        if (response.data.success) {
          notification.success({ message: 'Claimed', description: 'Job claimed successfully.' });
          fetchAll();
        } else {
          notification.error({ message: 'Already Claimed', description: response.data.message });
          fetchAll();
        }
      })
      .catch(() => notification.error({ message: 'Error', description: 'Something went wrong.' }));
  };

  const handleRelease = jobId => {
    releaseJobFromBiller(jobId)
      .then(response => {
        if (response.data.success) {
          notification.success({ message: 'Released', description: 'Job released back to group queue.' });
          fetchAll();
        } else {
          notification.error({ message: 'Failed', description: response.data.message });
        }
      })
      .catch(() => notification.error({ message: 'Error', description: 'Something went wrong.' }));
  };

  const fetchAll = () => {
    setLoading(true);
    billiersJobList('', 10000, 0, '', '')
      .then(response => {
        setLoading(false);
        if (response.data.success) {
          setBillers(response.data.result);
        } else {
          notification.error({
            message: 'Failed',
            description: response.data.message
          });
        }
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const filteredBillers = useMemo(() => {
    const [start, end] = dateRange;
    if (!start && !end) return billers;
    const from = start ? start.clone().startOf('day').valueOf() : null;
    const till = end ? end.clone().endOf('day').valueOf() : null;
    return billers.filter(row => {
      if (!row.submissionTime) return false;
      const time = new Date(row.submissionTime).valueOf();
      if (Number.isNaN(time)) return false;
      if (from !== null && time < from) return false;
      if (till !== null && time > till) return false;
      return true;
    });
  }, [billers, dateRange]);

  const fetchColumnPreferences = useCallback(async () => {
    try {
      const response = await getColumnPreferences(BILLER_PAGE_NAME);
      if (
        response.data &&
        response.data.result &&
        Array.isArray(response.data.result) &&
        response.data.result.length > 0
      ) {
        const columnPreferences = response.data.result.reduce((acc, item) => {
          if (item.columnName && item.columnName in BILLER_DEFAULT_COLUMNS) {
            acc[item.columnName] = item.selected;
          }
          return acc;
        }, {});

        const updatedColumns = {
          ...BILLER_DEFAULT_COLUMNS,
          ...columnPreferences,
          jobId: true,
          actions: true
        };

        setVisibleColumns(updatedColumns);
      }
    } catch (error) {
      console.error('Failed to fetch column preferences:', error);
    }
  }, []);

  const handleSaveColumnSettings = newVisibleColumns => {
    setVisibleColumns(newVisibleColumns);

    const columnPreferencesArray = Object.keys(newVisibleColumns).map(columnName => ({
      columnName,
      selected: newVisibleColumns[columnName]
    }));

    saveColumnPreferences(BILLER_PAGE_NAME, columnPreferencesArray)
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Column preferences saved successfully'
        });
        fetchColumnPreferences();
      })
      .catch(error => {
        console.error('Failed to save column preferences:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to save column preferences. Please try again.'
        });
      });
  };

  const viewCompleteBillerJob = useCallback(
    jobId => {
      assignJobtobiller(jobId)
        .then(response => {
          if (response.data.success) {
            sessionStorage.setItem(`autoAssigned_${jobId}`, 'true');
            history.push(
              `/dashboard/billers-jobs/${jobId}?sessionId=${sessionId}`
            );
          } else {
            notification.error({
              message: 'Failed',
              description: response.data.message
            });
          }
        })
        .catch(error => {
          console.error('Error assigning job:', error);
          notification.error({
            message: 'Error',
            description: 'Something went wrong while assigning the job.'
          });
        });
    },
    [history, sessionId]
  );

  return (
    <>
      <PageTitle title='Jobs' />
      <ContainerRow gutter={[0, 40]}>
        <StyledListTab
          style={{ width: '100%' }}
          activeKey={activeTab}
          onChange={key => {
            setActiveTab(key);
            setSearchVal('');
            setGroupQueueSearchVal('');
            setClearFiltersTrigger(t => t + 1);
          }}
        >
          <StyledJobListTabPane tab='My Jobs' key='myJobs'>
            <TitleRow justify='space-between' style={{ paddingLeft: 20, paddingRight: 20 }}>
              <CustomTypography color={theme['@text-color']}>Jobs</CustomTypography>
              <ControlsDiV>
                <DatePicker.RangePicker
                  value={pickerDisplayValue}
                  onOpenChange={open => {
                    if (open) setPickerDisplayValue([null, null]);
                  }}
                  onChange={dates => {
                    const newDates = dates || [null, null];
                    setPickerDisplayValue(newDates);
                    setDateRange(newDates);
                  }}
                  format='YYYY-MM-DD'
                  allowClear
                  disabledDate={current => current && current > moment().endOf('day')}
                  style={{ padding: '6px 12px', marginRight: '10px' }}
                />
                <SearchJobListStyle
                  prefix={<SearchOutlined />}
                  placeholder='Search Jobs'
                  onChange={event => setSearchVal(event.target.value)}
                  value={searchVal}
                />
                <SettingsButton
                  icon={<DownloadOutlined />}
                  onClick={() => exportFunction && exportFunction()}
                >
                  Export to Excel
                </SettingsButton>
                <SettingsButton
                  icon={<SettingOutlined />}
                  onClick={() => setColumnSettingsVisible(true)}
                >
                  Columns
                </SettingsButton>
                <SettingsButton
                  onClick={() => {
                    setSearchVal('');
                    setClearFiltersTrigger(t => t + 1);
                  }}
                >
                  Clear Filter
                </SettingsButton>
              </ControlsDiV>
            </TitleRow>

            <StyledJobListTableContainer style={{ margin: 0, width: '100%' }}>
              <BillerTanStackTable
                data={filteredBillers}
                loading={loading}
                searchValue={searchVal}
                viewCompleteBillerJob={viewCompleteBillerJob}
                onExportReady={exportFn => setExportFunction(() => exportFn)}
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                clearFiltersTrigger={clearFiltersTrigger}
              />
            </StyledJobListTableContainer>
          </StyledJobListTabPane>

          <StyledJobListTabPane tab='Group Queue' key='groupQueue'>
            <TitleRow justify='space-between' style={{ paddingLeft: 20, paddingRight: 20 }}>
              <CustomTypography color={theme['@text-color']}>Group Queue</CustomTypography>
              <ControlsDiV>
                <SearchJobListStyle
                  prefix={<SearchOutlined />}
                  placeholder='Search Group Queue'
                  onChange={event => setGroupQueueSearchVal(event.target.value)}
                  value={groupQueueSearchVal}
                />
                <SettingsButton
                  onClick={() => {
                    setGroupQueueSearchVal('');
                    setClearFiltersTrigger(t => t + 1);
                  }}
                >
                  Clear Filter
                </SettingsButton>
              </ControlsDiV>
            </TitleRow>
            <StyledJobListTableContainer style={{ margin: 0, width: '100%' }}>
              <TanStackUserTable
                data={groupQueueJobs}
                columns={createGroupQueueColumns({ onClaim: handleClaim, onRelease: handleRelease, userId: user.id })}
                loading={loading}
                searchValue={groupQueueSearchVal}
                storageKey='biller_group_queue'
                clearFiltersTrigger={clearFiltersTrigger}
              />
            </StyledJobListTableContainer>
          </StyledJobListTabPane>
        </StyledListTab>
      </ContainerRow>

      <BillerColumnSettingsModal
        visible={columnSettingsVisible}
        onCancel={() => setColumnSettingsVisible(false)}
        onSave={handleSaveColumnSettings}
        visibleColumns={visibleColumns}
      />
    </>
  );
};

export default Biller;
